import {
  NewPropertyFields,
  generateComprehensiveHeaderMapping,
} from "@/constants/propertyFields";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldButton } from "@emerald-react/button";
import { EmptyState } from "@emerald-react/empty-state";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldList, EmeraldListItem } from "@emerald-react/list";
import { EmeraldMenu } from "@emerald-react/menu";
import { EmeraldNotification } from "@emerald-react/notification";
import { EmeraldButtonVariant } from "@emerald/nxcore";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useConfirmation } from "@hooks/useConfirmation";
import { useSortableList } from "@hooks/useSortable";
import {
  useAddNewPropertyMutation,
  useBulkImportPropertiesMutation,
  useImportPropertiesFromExcelMutation,
  useParsePropertiesMutation,
  useReorderPropertyMutation,
  useUpdatePropertyMutation,
  useUpdatePropertySortOrderMutation,
} from "@services/survey-properties";
import { useUpdateSurveyMutation } from "@services/survey-core";
import { scrollToSection } from "@shared/utils";
import type { PropertySortOrder } from "@survey-types/survey.core.types";
import type { PropertyWithOrder } from "@survey-types/survey.property.types";
import {
  selectCurrentSurvey,
  selectCurrentSurveyId,
  selectPropertiesWithOrder,
} from "@survey/surveySelectors";
import { currentPropertyId, reorderProperties, setOpenPropertiesDrawer, updateCurrentSurvey } from "@survey/surveySlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ColumnMatchingModal from "./ColumnMatchingModal";
import PropertyListItem from "./PropertyListItem";
import RowReportModal from "./RowReportModal";

// Options shown in the dropdown (Custom order is NOT selectable — it is set automatically on manual drag)
const SORT_OPTIONS: { value: PropertySortOrder; label: string }[] = [
  { value: "PROJECT_ORDER", label: "Project order" },
  { value: "NORTH_TO_SOUTH", label: "North to South" },
  { value: "SOUTH_TO_NORTH", label: "South to North" },
  { value: "EAST_TO_WEST", label: "East to West" },
  { value: "WEST_TO_EAST", label: "West to East" },
];

const SORT_LABELS: Record<PropertySortOrder, string> = {
  CUSTOM_SORT: "Custom order",
  PROJECT_ORDER: "Project order",
  NORTH_TO_SOUTH: "North to South",
  SOUTH_TO_NORTH: "South to North",
  EAST_TO_WEST: "East to West",
  WEST_TO_EAST: "West to East",
};

const PropertiesListDrawer = () => {
  const dispatch = useAppDispatch();
  const survey = useAppSelector((state) => state.survey.currentSurvey);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(
    new Set(),
  );
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [showColumnMatching, setShowColumnMatching] = useState(false);
  const [showRowReport, setShowRowReport] = useState(false);
  const [importResult, setImportResult] = useState<{
    matched: string[];
    notMatched: string[];
    unParsedColumns: string[][];
  } | null>(null);
  const [importSummary, setImportSummary] = useState<{
    toImport: number;
    notImported: number;
    totalProperties: number;
    geocodingErrors?: number;
  } | null>(null);
  const [parsedPropertiesData, setParsedPropertiesData] = useState<any>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // RTK Query hooks with automatic loading states
  const [reorderProperty] = useReorderPropertyMutation();
  const [addNewProperty] = useAddNewPropertyMutation();
  const [updateProperty] = useUpdatePropertyMutation();
  const [updateSurvey] = useUpdateSurveyMutation();
  const [importPropertiesFromExcel, { isLoading: isUploading }] =
    useImportPropertiesFromExcelMutation();
  const [parseProperties, parseResult] = useParsePropertiesMutation();
  const [bulkImportProperties, { isLoading: isImporting }] =
    useBulkImportPropertiesMutation();
  const [updatePropertySortOrder] = useUpdatePropertySortOrderMutation();

  const currentSurvey = useAppSelector(selectCurrentSurvey);
  const propertiesWithOrder = useAppSelector(selectPropertiesWithOrder);
  const currentSurveyId = useAppSelector(selectCurrentSurveyId);
  const isPropertiesDrawerOpen = useAppSelector(
    (state) => state.survey.isPropertiesDrawerOpen,
  );
  const selectablePropertyIds = useMemo(
    () =>
      propertiesWithOrder
        .filter((p) => !p.isClientShortlist && !p.isHidden)
        .map((p) => p.id),
    [propertiesWithOrder],
  );
  const selectablePropertyIdSet = useMemo(
    () => new Set(selectablePropertyIds),
    [selectablePropertyIds],
  );

  const propertySortOrder: PropertySortOrder =
    currentSurvey?.propertySortOrder ?? "PROJECT_ORDER";
  const currentSortLabel = SORT_LABELS[propertySortOrder] ?? "Project order";

  const { withLoading } = useSimpleConfirmation();
  const { confirmAction } = useConfirmation();

  // Derived checkbox states — always in sync with selectedProperties
  const selectedSelectableCount = useMemo(
    () =>
      Array.from(selectedProperties).filter((id) =>
        selectablePropertyIdSet.has(id),
      ).length,
    [selectedProperties, selectablePropertyIdSet],
  );
  const selectAllChecked =
    selectablePropertyIds.length > 0 &&
    selectedSelectableCount === selectablePropertyIds.length;
  const isIndeterminate =
    selectedSelectableCount > 0 &&
    selectedSelectableCount < selectablePropertyIds.length;

  // Sync indeterminate state on native checkbox (not settable via JSX prop)
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Reset checkbox selections when drawer state changes (opening or closing)
  useEffect(() => {
    setSelectedProperties(new Set());
  }, [isPropertiesDrawerOpen]);

  // Keep only selectable IDs in state (e.g. when shortlist status changes).
  useEffect(() => {
    setSelectedProperties((prev) => {
      const next = new Set(
        Array.from(prev).filter((id) => selectablePropertyIdSet.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [selectablePropertyIdSet]);

  // Check if any property has invalid coordinates
  const hasInvalidCoordinates = propertiesWithOrder.some((property) => {
    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);

    const isLatInvalid =
      isNaN(lat) || lat < -90 || lat > 90 || property.latitude.trim() === "";
    const isLngInvalid =
      isNaN(lng) || lng < -180 || lng > 180 || property.longitude.trim() === "";
    const isZeroZero = lat === 0 && lng === 0;

    return isLatInvalid || isLngInvalid || isZeroZero;
  });

  // Centralized survey validation - early return if no survey ID
  if (!currentSurveyId) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4 mx-auto">
              <span className="text-2xl text-slate-400">⚠️</span>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No Survey Selected
            </h3>
            <p className="text-slate-500">
              Please select or create a survey to manage properties
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSortChange = (sortOrder: PropertySortOrder) => {
    if (!currentSurveyId || sortOrder === propertySortOrder) return;
    updatePropertySortOrder({
      surveyId: currentSurveyId,
      propertySortOrder: sortOrder,
    });
  };

  const handleReorder = (
    _reorderedProperties: PropertyWithOrder[],
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    // Only proceed if we have properties for reordering
    if (propertiesWithOrder.length === 0) {
      console.error("Cannot reorder: No properties available");
      return;
    }

    dispatch(
      reorderProperties({
        sourceIndex,
        destinationIndex,
      }),
    );
    reorderProperty({
      surveyId: currentSurveyId,
      order: destinationIndex,
      propertyId: propertiesWithOrder[sourceIndex].id,
    });
    // BE automatically sets propertySortOrder to CUSTOM_SORT on reorder;
    // the reorderProperty invalidatesTags will refetch getSurvey with the updated value.
  };

  const { DndContextProps, SortableContextProps } = useSortableList({
    items: propertiesWithOrder,
    onReorder: handleReorder,
    idField: "id",
  });

  const handleAddProperty = async () => {
    try {
      const result = await addNewProperty({
        AddPropertyFields: {
          ...NewPropertyFields,
          order: propertiesWithOrder.length,
          surveyId: currentSurveyId,
        },
      }).unwrap();

      dispatch(currentPropertyId({ propertyId: result?.propertyId || null }));
    } catch (_error) {
      console.error("Failed to add property");
    }
  };

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedProperties(new Set(selectablePropertyIds));
      } else {
        setSelectedProperties(new Set());
      }
    },
    [selectablePropertyIds],
  );

  const handleToggleSelect = useCallback(
    (id: string, selected: boolean) => {
      if (!selectablePropertyIdSet.has(id)) {
        return;
      }

      setSelectedProperties((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [selectablePropertyIdSet],
  );

    const handleResetShortlist = async () => {
    const shortlistedProperties = propertiesWithOrder.filter(
      (p) => p.isClientShortlist,
    );
    if (shortlistedProperties.length === 0) return;

    const confirmed = await confirmAction(
      "Reset Shortlist Notification",
      `Are you sure you want to reset the shortlist notification email?`,
      {
        icon: undefined,
        customClass: { popup: "swal-no-icon" },
        confirmButtonText: "Yes, Reset",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#003f2d",
        cancelButtonColor: "#6c757d",
        reverseButtons: true,
        focusCancel: true,
      },
    );
    if (!confirmed) return;

    // Immediately update Redux store for responsive UI
    dispatch(updateCurrentSurvey({ emailNotificationStatus: "NOT_SENT" }));
    // Patch survey via API — invalidates Survey tags so getSurvey refetches the actual server value
    if (currentSurveyId) {
      updateSurvey({ id: currentSurveyId, emailNotificationStatus: "NOT_SENT" });
    }

    // Patch each shortlisted property: set isClientShortlist to false
    shortlistedProperties.forEach((p) => {
      updateProperty({ propertyId: p.propertyId, updates: { isClientShortlist: false } });
    });
  };

  const handleStylePinsClick = () => {
    // Get the first selected property ID
    const firstSelectedPropertyId = Array.from(selectedProperties)[0];
    // Visible properties in list order (include current location for multi-select styling)
    const visibleProperties = propertiesWithOrder.filter((p) => !p.isHidden);

    // Build pin data using the visible index (matches displayed pin numbers)
    const selectedPinsData = visibleProperties
      .map((prop, visibleIdx) => ({
        prop,
        number: prop.isCurrentLocation ? -1 : visibleIdx + 1,
      }))
      .filter(({ prop }) => selectedProperties.has(prop.id))
      .map(({ prop, number }) => ({ number, id: prop.propertyId }));

    // Store selected pin info for the style editor
    if (selectedPinsData.length > 0) {
      sessionStorage.setItem(
        "selectedPinInfo",
        JSON.stringify({
          numbers: selectedPinsData.map((p) => p.number),
          ids: selectedPinsData.map((p) => p.id),
          type: "pin",
        }),
      );
    }

    // Close the properties drawer
    dispatch(setOpenPropertiesDrawer(false));

    // Find the market map section
    const marketMapSection = survey?.sections?.find((section) =>
      section.name?.toLowerCase().replace(/[_-]/g, " ").includes("market map"),
    );

    if (marketMapSection) {
      // Scroll to the market map section
      setTimeout(() => {
        scrollToSection(marketMapSection.surveySectionId);

        // Open the accordion by clicking its header
        setTimeout(() => {
          const accordionHeader = document.querySelector(
            `[data-section-id="${marketMapSection.surveySectionId}"] .accordion-header`,
          );
          if (accordionHeader && accordionHeader instanceof HTMLElement) {
            accordionHeader.click();
          }

          // After opening the accordion, open the style editor for the first selected property
          if (firstSelectedPropertyId) {
            setTimeout(() => {
              // Find the marker element for the selected property
              const markerElement = document.getElementById(
                firstSelectedPropertyId,
              );
              if (markerElement) {
                // Click the marker to open its popup
                markerElement.click();

                // Wait for popup to appear, then click the style button
                setTimeout(() => {
                  const styleButton = document.querySelector(
                    "#utility-style-property",
                  );
                  if (styleButton && styleButton instanceof HTMLElement) {
                    styleButton.click();
                  }
                }, 200);
              }
            }, 300);
          }
        }, 100);
      }, 100);
    }
  };

  const handleUploadFromExcel = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".xls")
    ) {
      alert("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    // Reset all import-related state before starting new import
    setImportResult(null);
    setImportSummary(null);
    setParsedPropertiesData(null);
    setShowColumnMatching(false);
    setShowRowReport(false);

    // Reset RTK Query state by calling reset if available
    if (parseResult.reset) {
      parseResult.reset();
    }

    try {
      const result = await importPropertiesFromExcel({
        file,
        surveyId: currentSurveyId,
      }).unwrap();
      setImportResult(result);
      setShowColumnMatching(true);
    } catch (error) {
      alert("Failed to import Excel file. Please try again.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleColumnMatchingNext = (userMappings: Record<string, string>) => {
    if (!importResult) return;

    // Generate comprehensive header mapping including all business fields
    const defaultHeaderMapping = generateComprehensiveHeaderMapping();

    // Convert user mappings to header mapping format
    const userHeaderMapping = Object.entries(userMappings).map(
      ([header, matchedAttribute]) => ({
        header,
        matchedAttribute,
      }),
    );

    // Create a map of headers that user has explicitly mapped
    const userMappedHeaders = new Set(Object.keys(userMappings));

    // Filter out default mappings that user has explicitly overridden
    const filteredDefaultMapping = defaultHeaderMapping.filter(
      (mapping) => !userMappedHeaders.has(mapping.header),
    );

    // Combine user mappings with remaining default mappings
    const finalHeaderMapping = [
      ...userHeaderMapping,
      ...filteredDefaultMapping,
    ];

    // No try-catch needed - RTK Query handles errors
    parseProperties({
      unParsedColumns: importResult.unParsedColumns,
      headerMapping: finalHeaderMapping,
    });
  };

  // Handle parse success/error with useEffect to ensure Row Report modal opens
  useEffect(() => {
    if (parseResult.isSuccess && parseResult.data) {
      setParsedPropertiesData(parseResult.data);

      const propertiesArray =
        (parseResult.data as any)?.geocodedPropertyDocs || [];
      const geocodingErrors = (parseResult.data as any)?.geocodingErrors || [];

      setImportSummary({
        toImport: propertiesArray.length,
        notImported: geocodingErrors.length,
        totalProperties: propertiesArray.length + geocodingErrors.length,
        geocodingErrors: geocodingErrors.length,
      });

      // Close column matching modal and open row report modal
      setShowColumnMatching(false);
      setShowRowReport(true);
    }
    if (parseResult.isError) {
      alert("Failed to parse properties. Please try again.");
    }
  }, [
    parseResult.isSuccess,
    parseResult.isError,
    parseResult.data,
    importResult,
  ]);

  const handleRowReportImport = async () => {
    if (!parsedPropertiesData) {
      return;
    }

    try {
      const properties = parsedPropertiesData?.geocodedPropertyDocs || [];

      if (properties.length === 0) {
        return;
      }

      // Simplified property formatting - let the backend handle ordering
      const formattedProperties = properties.map((property: any) => {
        const currentDate = new Date().toISOString();
        const customFields = Object.entries(property.$custom || {}).map(
          ([name, value]) => ({
            customField: { name },
            value: String(value || ""),
            updatedOn: currentDate,
          }),
        );

        return {
          ...property,
          customFields,
          // Remove order field - let backend handle proper ordering
        };
      });

      // RTK Query handles the complexity - just call the mutation
      withLoading(
        async () =>
          await bulkImportProperties({
            surveyId: currentSurveyId,
            properties: formattedProperties,
          }).unwrap(),
        {
          successText: "Property has been successfully imported!",
        },
      ).catch((_error) => {
        console.error("Failed to import property");
      });

      handleModalClose();
    } catch (_error) {
      console.error("Error importing properties");
    }
  };

  const handleModalClose = () => {
    setShowRowReport(false);
    setShowColumnMatching(false);
    setImportResult(null);
    setImportSummary(null);
    setParsedPropertiesData(null);
  };

  return (
    <>
      {/* Success Notification */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50">
          <EmeraldNotification
            variant="success"
            closable={true}
            title="Success"
            message="Property has been successfully imported"
            onClose={() => setShowSuccessMessage(false)}
          />
        </div>
      )}

      <div className="h-full flex flex-col bg-white">
        {/* Header with action buttons */}
        <div className="px-4 pb-4 border-b border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
            {/* Sort order dropdown */}
            <EmeraldMenu
              triggerElement={
                <button
                  className="flex items-center gap-1 text-sm font-medium text-cbre-green hover:text-cbre-green/80 transition-colors"
                  type="button"
                >
                  <EmeraldIcon
                    icon="swap_vert"
                    fontsizeInRem={1.1}
                    variant="icon"
                  />
                  <span>Reorder: {currentSortLabel}</span>
                </button>
              }
              opened={false}
              closeOnOutsideClick={true}
              position="auto"
              dropdownWidth={220}
            >
              <EmeraldList>
                {SORT_OPTIONS.map((option) => (
                  <EmeraldListItem
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                  >
                    <div className="flex items-center gap-2 w-full whitespace-nowrap">
                      <span className="w-5 flex items-center justify-center">
                        {propertySortOrder === option.value && (
                          <EmeraldIcon
                            icon="check"
                            fontsizeInRem={1}
                            variant="icon"
                          />
                        )}
                      </span>
                      <span>{option.label}</span>
                    </div>
                  </EmeraldListItem>
                ))}
              </EmeraldList>
            </EmeraldMenu>
            
            <EmeraldButton
                label="Reset Shortlist"
                variant={EmeraldButtonVariant.Secondary}
                onClick={handleResetShortlist}
                disabled={!propertiesWithOrder.some(p => p.isClientShortlist)}
              />
            </div>


            <div className="flex items-center gap-3">
              <EmeraldButton
                id="SC-CreateNewProperty"
                icon="add"
                label="New Property"
                variant={EmeraldButtonVariant.TextButton}
                onClick={handleAddProperty}
              />
              <EmeraldButton
                icon="cloud_upload"
                iconFillType="outlined"
                label={
                  isUploading ? "Uploading..." : "Upload Properties From Excel"
                }
                onClick={handleUploadFromExcel}
                disabled={isUploading}
                id="SC-UploadPropertiesfromExcel"
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
              />
            </div>
          </div>
        </div>

        {/* Checkbox and Style Pins button */}
        {propertiesWithOrder.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
            {/* Left spacing to align with checkbox in list items (accounts for drag handle) */}
            <div style={{ width: "30px" }} />
            {/* Native checkbox — derived state, never stale */}
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={selectAllChecked}
              disabled={selectablePropertyIds.length === 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className={
                selectablePropertyIds.length === 0
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              }
              style={{ width: "16px", height: "16px", accentColor: "#003f2d" }}
            />
            <EmeraldButton
              label="Style Pins"
              variant={EmeraldButtonVariant.Secondary}
              onClick={handleStylePinsClick}
              disabled={selectedSelectableCount === 0}
            />
          </div>
        )}

        {hasInvalidCoordinates && (
          <div
            style={{ backgroundColor: "#FFF2E5", borderColor: "#D5710C" }}
            className="mx-4 my-2 px-4 py-3 border rounded-sm flex items-center gap-2"
          >
            <EmeraldIcon
              icon="warning_amber"
              type="filled"
              fontsizeInRem={1.2}
              className="text-orange-600"
              variant="icon"
            />
            <span
              className="text-warning-dark"
              style={{ fontSize: "0.875rem", fontWeight: 100 }}
            >
              Invalid or missing coordinates data. Please update the marked
              properties with valid coordinates.
            </span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {propertiesWithOrder.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <EmptyState
                title={"No Properties yet"}
                description={
                  "Add your first property to start building your survey"
                }
                status={"no_data"}
                position="center"
              />
            </div>
          ) : (
            <DndContext {...DndContextProps}>
              <SortableContext {...SortableContextProps}>
                <div className="divide-y divide-divider-4">
                  {propertiesWithOrder.map((property, mapIndex) => {
                    const visiblePropsBeforeThis = propertiesWithOrder
                      .slice(0, mapIndex)
                      .filter(
                        (p) => !p.isHidden && !p.isCurrentLocation,
                      ).length;

                    return (
                      <PropertyListItem
                        key={property.id}
                        property={property}
                        index={visiblePropsBeforeThis}
                        isDraggable={true}
                        isSelected={selectedProperties.has(property.id)}
                        isSelectionDisabled={
                          property.isClientShortlist || property.isHidden
                        }
                        selectionDisabledMessage={
                          property.isClientShortlist
                            ? "Point styling is not enabled on shortlisted properties."
                            : property.isHidden
                              ? "Point styling is not enabled on hidden properties."
                              : undefined
                        }
                        onToggleSelect={handleToggleSelect}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <ColumnMatchingModal
        isOpen={showColumnMatching}
        onClose={handleModalClose}
        onNext={handleColumnMatchingNext}
        importResult={importResult}
        isProcessing={parseResult.isLoading}
      />

      <RowReportModal
        isOpen={showRowReport}
        onClose={handleModalClose}
        onImport={handleRowReportImport}
        importData={importSummary}
        isImporting={isImporting}
      />
    </>
  );
};

export default PropertiesListDrawer;
